import { useState, useEffect, useCallback } from 'react'
import GlobalSearch from './GlobalSearch'
import { UserPlus, Check, X, Users, Clock } from 'lucide-react'
import { useFriendRequests } from '../hooks/useFriendRequests'
import { supabase } from '../lib/supabase'

/**
 * SessionTimer Component
 * Shows elapsed time since channel became active
 */
function SessionTimer({ startTime }) {
  const [elapsed, setElapsed] = useState('')

  useEffect(() => {
    if (!startTime) return

    const updateTimer = () => {
      const start = new Date(startTime).getTime()
      const now = Date.now()
      const diff = Math.floor((now - start) / 1000)

      const hours = Math.floor(diff / 3600)
      const minutes = Math.floor((diff % 3600) / 60)
      const seconds = diff % 60

      if (hours > 0) {
        setElapsed(`${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`)
      } else {
        setElapsed(`${minutes}:${seconds.toString().padStart(2, '0')}`)
      }
    }

    updateTimer()
    const interval = setInterval(updateTimer, 1000)

    return () => clearInterval(interval)
  }, [startTime])

  if (!startTime || !elapsed) return null

  return (
    <div className="flex items-center gap-1 text-[10px] text-emerald-400/70" title="Session duration">
      <Clock className="w-3 h-3" />
      <span className="font-mono">{elapsed}</span>
    </div>
  )
}

/**
 * Panel 2: Channels/Friends Sidebar
 * Middle panel (w-60 desktop) displaying either:
 * - Channels within the selected Space
 * - Friends list when in DM mode
 */
export default function ChannelsSidebar({
  viewMode,
  selectedSpace,
  channels,
  selectedChannel,
  onSelectChannel,
  friends,
  profile,
  mobileMenuOpen,
  userId,
  onSearchResultSelect,
  toast,
  channelUsers = {}, // Map of channelId -> array of users in that channel
  channelSessions = {}, // Map of channelId -> { startTime, sessionId }
}) {
  const [searchQuery, setSearchQuery] = useState('')
  const [friendFilter, setFriendFilter] = useState('all') // 'all', 'online', 'pending', 'blocked'
  const [pendingRequests, setPendingRequests] = useState([])

  // Use friend requests hook
  const friendRequests = useFriendRequests(userId, toast)

  // Load pending friend requests
  useEffect(() => {
    if (viewMode === 'dms' && userId) {
      loadPendingRequests()
    }
  }, [viewMode, userId])

  const loadPendingRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('friendships')
        .select('*, profiles!friendships_user_id_fkey(id, username, avatar_url)')
        .eq('friend_id', userId)
        .eq('status', 'pending')

      if (!error && data) {
        setPendingRequests(data)
      }
    } catch (err) {
      console.error('Error loading pending requests:', err)
    }
  }

  const handleAcceptRequest = async (request) => {
    await friendRequests.acceptRequest(request.id, request.profiles.username)
    loadPendingRequests()
  }

  const handleRejectRequest = async (request) => {
    await friendRequests.rejectRequest(request.id)
    loadPendingRequests()
  }

  // Filter channels based on search
  const filteredChannels = channels.filter((channel) =>
    channel.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // Group channels by type
  const channelsByType = {
    text: filteredChannels.filter((c) => c.type === 'text'),
    voice: filteredChannels.filter((c) => c.type === 'voice'),
    media: filteredChannels.filter((c) => c.type === 'media'),
  }

  // Filter friends
  const filteredFriends = friends.filter((friend) => {
    const friendProfile = friend.profiles
    if (!friendProfile) return false

    // Apply status filter
    if (friendFilter === 'online' && friendProfile.status !== 'online') return false
    if (friendFilter === 'pending' && friend.status !== 'pending') return false
    if (friendFilter === 'blocked' && friend.status !== 'blocked') return false

    // Apply search
    if (searchQuery && !friendProfile.username.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false
    }

    return true
  })

  return (
    <div
      className={`
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        md:translate-x-0
        fixed md:relative
        left-16 md:left-0
        z-30
        h-full w-60
        bg-black/20 backdrop-blur-md border-r border-white/5
        flex flex-col
        transition-transform duration-300
      `}
    >
      {/* Global Search */}
      <div className="p-3 border-b border-white/5">
        <GlobalSearch userId={userId} onSelectResult={onSearchResultSelect} toast={toast} />
      </div>

      {/* Header */}
      <div className="p-4 border-b border-white/5">
        {viewMode === 'spaces' && selectedSpace ? (
          <>
            <h2 className="text-white font-semibold mb-1 truncate">{selectedSpace.name}</h2>
            <p className="text-xs text-slate-400 truncate">{selectedSpace.description || 'No description'}</p>
          </>
        ) : (
          <h2 className="text-white font-semibold">Direct Messages</h2>
        )}
      </div>

      {/* Local Search Bar (for filtering current view) */}
      <div className="p-3 border-b border-white/5">
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={viewMode === 'spaces' ? 'Search channels...' : 'Search friends...'}
            className="w-full pl-9 pr-3 py-2 text-sm glass rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-gjyl-indigo-500/50"
          />
          <svg
            className="absolute left-3 top-2.5 w-4 h-4 text-slate-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto scrollbar-gjyl">
        {viewMode === 'spaces' ? (
          // Channels View
          <div className="p-3 space-y-4">
            {/* Text Channels */}
            {channelsByType.text.length > 0 && (
              <ChannelGroup
                title="TEXT CHANNELS"
                icon="#"
                channels={channelsByType.text}
                selectedChannel={selectedChannel}
                onSelectChannel={onSelectChannel}
                channelUsers={channelUsers}
                channelSessions={channelSessions}
                showUsers={false}
              />
            )}

            {/* Voice Channels */}
            {channelsByType.voice.length > 0 && (
              <ChannelGroup
                title="VOICE CHANNELS"
                icon="🔊"
                channels={channelsByType.voice}
                selectedChannel={selectedChannel}
                onSelectChannel={onSelectChannel}
                channelUsers={channelUsers}
                channelSessions={channelSessions}
                showUsers={true}
              />
            )}

            {/* Media Channels */}
            {channelsByType.media.length > 0 && (
              <ChannelGroup
                title="MEDIA CHANNELS"
                icon="📺"
                channels={channelsByType.media}
                selectedChannel={selectedChannel}
                onSelectChannel={onSelectChannel}
                channelUsers={channelUsers}
                channelSessions={channelSessions}
                showUsers={true}
              />
            )}

            {filteredChannels.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-8">
                No channels found
              </p>
            )}
          </div>
        ) : (
          // Friends View
          <div className="p-3">
            {/* Pending Friend Requests */}
            {pendingRequests.length > 0 && (
              <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-blue-400" />
                  <span className="text-sm font-medium text-blue-400">
                    Pending Requests ({pendingRequests.length})
                  </span>
                </div>
                <div className="space-y-2">
                  {pendingRequests.map((request) => (
                    <div key={request.id} className="flex items-center gap-2 py-2 border-b border-blue-500/10 last:border-0">
                      <div className="relative flex-shrink-0">
                        <img
                          src={request.profiles?.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${request.user_id}`}
                          alt={request.profiles?.username}
                          className="w-8 h-8 rounded-full"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm text-white truncate block">
                          @{request.profiles?.username || 'Unknown'}
                        </span>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <button
                          onClick={() => handleAcceptRequest(request)}
                          disabled={friendRequests.loading}
                          className="p-1.5 bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 rounded text-green-400 transition-all disabled:opacity-50"
                          title="Accept"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleRejectRequest(request)}
                          disabled={friendRequests.loading}
                          className="p-1.5 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded text-red-400 transition-all disabled:opacity-50"
                          title="Decline"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Friend Filter Tabs */}
            <div className="flex space-x-2 mb-4">
              {['all', 'online', 'pending'].map((filter) => (
                <button
                  key={filter}
                  onClick={() => setFriendFilter(filter)}
                  className={`
                    px-3 py-1 rounded-lg text-xs font-medium transition-all
                    ${friendFilter === filter
                      ? 'bg-gjyl-indigo-500 text-white'
                      : 'glass text-slate-400 hover:text-white'
                    }
                  `}
                >
                  {filter.charAt(0).toUpperCase() + filter.slice(1)}
                </button>
              ))}
            </div>

            {/* Friends List */}
            <div className="space-y-1">
              {filteredFriends.map((friend) => {
                const friendProfile = friend.profiles
                return (
                  <button
                    key={friend.id}
                    className="w-full flex items-center space-x-3 p-2 rounded-lg hover:bg-gjyl-slate-700/50 transition-all group"
                  >
                    {/* Avatar with Status */}
                    <div className="relative flex-shrink-0">
                      <div className="w-8 h-8 rounded-full overflow-hidden">
                        {friendProfile.avatar_url ? (
                          <img
                            src={friendProfile.avatar_url}
                            alt={friendProfile.username}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gjyl-slate-700 flex items-center justify-center text-xs text-white font-semibold">
                            {friendProfile.username.substring(0, 2).toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-gjyl-slate-800 status-${friendProfile.status || 'offline'}`}></div>
                    </div>

                    {/* Username */}
                    <div className="flex-1 text-left">
                      <p className="text-sm text-slate-300 group-hover:text-white transition-colors truncate">
                        {friendProfile.username}
                      </p>
                      {friendProfile.spotify_current_track && (
                        <p className="text-xs text-slate-500 truncate">
                          🎧 {friendProfile.spotify_current_track.track}
                        </p>
                      )}
                    </div>
                  </button>
                )
              })}

              {filteredFriends.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-8">
                  No friends found
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      {viewMode === 'dms' && (
        <div className="p-3 border-t border-white/5">
          <button className="w-full btn-primary text-sm py-2">
            + Add Friend
          </button>
        </div>
      )}
    </div>
  )
}

// Channel Group Component with user presence display
function ChannelGroup({ title, icon, channels, selectedChannel, onSelectChannel, channelUsers = {}, channelSessions = {}, showUsers = false }) {
  return (
    <div>
      <h3 className="text-xs font-semibold text-slate-500 mb-2 px-2">
        {title}
      </h3>
      <div className="space-y-1">
        {channels.map((channel) => {
          const usersInChannel = channelUsers[channel.id] || []
          const sessionData = channelSessions[channel.id]
          const isVoiceOrMedia = channel.type === 'voice' || channel.type === 'media'
          const hasActiveUsers = usersInChannel.length > 0

          return (
            <div key={channel.id} className="space-y-1">
              <button
                onClick={() => onSelectChannel(channel)}
                className={`
                  w-full flex items-center space-x-2 px-2 py-1.5 rounded-lg
                  transition-all duration-200 group
                  ${selectedChannel?.id === channel.id
                    ? 'bg-white/10 text-white'
                    : 'text-slate-400 hover:bg-white/5 hover:text-white'
                  }
                `}
              >
                <span className="text-sm">{icon}</span>
                <span className="text-sm font-medium truncate flex-1 text-left">{channel.name}</span>

                {/* User count badge and session timer for voice/media channels */}
                {isVoiceOrMedia && hasActiveUsers && (
                  <div className="flex items-center gap-1.5">
                    {/* Session timer */}
                    <SessionTimer startTime={sessionData?.startTime} />

                    {/* User count badge */}
                    <span className="text-xs bg-gjyl-indigo-500/30 text-gjyl-indigo-300 px-1.5 py-0.5 rounded-full flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {usersInChannel.length}
                    </span>
                  </div>
                )}
              </button>

              {/* Show users in voice/media channels */}
              {showUsers && isVoiceOrMedia && hasActiveUsers && (
                <div className="ml-6 space-y-0.5">
                  {usersInChannel.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center gap-2 px-2 py-1 text-xs text-slate-400 hover:text-slate-300 transition-colors"
                    >
                      <div className="relative flex-shrink-0">
                        {user.avatar_url ? (
                          <img
                            src={user.avatar_url}
                            alt={user.username}
                            className="w-5 h-5 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-5 h-5 rounded-full bg-gjyl-slate-600 flex items-center justify-center text-[10px] text-white font-medium">
                            {(user.username || '?')[0].toUpperCase()}
                          </div>
                        )}
                        <div className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-green-500 rounded-full border border-gjyl-slate-800" />
                      </div>
                      <span className="truncate flex-1">{user.display_name || user.username}</span>
                      {user.isMuted && (
                        <span className="text-red-400" title="Muted">🔇</span>
                      )}
                      {user.isDeafened && (
                        <span className="text-red-400" title="Deafened">🔕</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
